import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { executiveAnalyticsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ClickableKpiCard, KpiDetailPanel } from './common/ClickableKpiCard';
import { BarChart3, Download, LifeBuoy, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from './ui/table-controls';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { CHART_COLORS, tooltipContentStyle, axisProps, gridProps } from '../lib/chartTheme';
import { EmptyState } from './common/States';

const PERIOD_OPTIONS = ['7d', '30d', '90d', 'custom'];
const GRANULARITY_OPTIONS = ['day', 'week', 'month'];

const ExecutiveAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30d');
  const [granularity, setGranularity] = useState('day');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [drilldownRows, setDrilldownRows] = useState([]);
  const [drilldownMetric, setDrilldownMetric] = useState('');
  const [detail, setDetail] = useState(null);
  const [exportPacks, setExportPacks] = useState([]);
  const drilldownTable = useTableControls(drilldownRows, {
    initialSortKey: null,
    initialSortDirection: 'asc',
  });

  const queryParams = useMemo(() => {
    const params = { period, granularity };
    if (period === 'custom' && customFromDate && customToDate) {
      params.from_date = customFromDate;
      params.to_date = customToDate;
    }
    return params;
  }, [period, granularity, customFromDate, customToDate]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryRes, trendRes, packsRes] = await Promise.all([
        executiveAnalyticsAPI.getSummary(queryParams),
        executiveAnalyticsAPI.getTrends(queryParams),
        executiveAnalyticsAPI.listExportPacks({ limit: 10 }),
      ]);
      setSummary(summaryRes.data?.summary || null);
      setTrend(trendRes.data?.trend || []);
      setExportPacks(packsRes.data || []);
    } catch (error) {
      toast.error('Failed to load executive analytics');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    if (period === 'custom' && (!customFromDate || !customToDate)) {
      return;
    }
    loadData();
  }, [period, customFromDate, customToDate, loadData]);

  const cards = useMemo(() => {
    const s = summary || {};
    return [
      {
        title: 'Active Headcount',
        value: s.operational?.active_headcount ?? 0,
        hint: 'Operational workforce size',
        icon: Users,
        metric: 'attendance_by_department',
        detail: 'Department-wise attendance marks for selected trend period.',
        moduleRoute: '/admin/employees',
      },
      {
        title: 'Attendance Rate',
        value: `${s.operational?.attendance_rate ?? 0}%`,
        hint: 'Operational attendance quality',
        icon: TrendingUp,
        metric: 'attendance_by_department',
        detail: 'Breakdown of attendance by department.',
        moduleRoute: '/admin/attendance',
      },
      {
        title: 'Open Fraud Alerts',
        value: s.operational?.open_fraud_alerts ?? 0,
        hint: 'Unresolved operational risk alerts',
        icon: BarChart3,
        metric: 'fraud_alerts_by_type',
        detail: 'Flag-type and severity split with unresolved counts.',
        moduleRoute: '/admin/fraud',
      },
      {
        title: 'Open Helpdesk',
        value: s.helpdesk?.tickets_open ?? 0,
        hint: 'Current unresolved workload',
        icon: LifeBuoy,
        metric: 'helpdesk_category_load',
        detail: 'Category load and breached tickets.',
        moduleRoute: '/admin/helpdesk',
      },
      {
        title: 'Survey Response Rate',
        value: `${s.engagement?.survey_response_rate ?? 0}%`,
        hint: 'Engagement participation',
        icon: BarChart3,
        metric: 'engagement_survey_performance',
        detail: 'Survey-wise participation and sentiment performance.',
        moduleRoute: '/admin/pulse-surveys',
      },
    ];
  }, [summary]);

  const openDetail = async (card) => {
    try {
      const res = await executiveAnalyticsAPI.getDrilldown({ metric: card.metric, ...queryParams });
      setDrilldownRows(res.data?.rows || []);
      setDrilldownMetric(res.data?.metric || card.metric);
      setDetail({
        title: card.title,
        value: card.value,
        description: card.detail,
        moduleRoute: card.moduleRoute,
      });
    } catch (error) {
      toast.error('Failed to load drill-down');
    }
  };

  const handleCreateExportPack = async () => {
    try {
      const res = await executiveAnalyticsAPI.createExportPack(queryParams);
      toast.success('Executive export pack generated');
      await loadData();
      const packId = res.data?.pack?.id;
      if (packId) {
        await handleDownloadPack(packId);
      }
    } catch (error) {
      toast.error('Failed to generate export pack');
    }
  };

  const handleDownloadPack = async (packId) => {
    try {
      const response = await executiveAnalyticsAPI.downloadExportPack(packId);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `executive-analytics-pack-${packId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to download export pack');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-hr-h2 font-bold">Executive Analytics</h2>
          <p className="text-muted-foreground text-hr-sm">
            Unified operational, engagement, and helpdesk metrics.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GRANULARITY_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {period === 'custom' && (
            <>
              <Input type="date" value={customFromDate} onChange={(e) => setCustomFromDate(e.target.value)} />
              <Input type="date" value={customToDate} onChange={(e) => setCustomToDate(e.target.value)} />
            </>
          )}
          <Button variant="outline" onClick={handleCreateExportPack}>
            <Download className="h-4 w-4 mr-2" />
            Export Pack
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <ClickableKpiCard
            key={card.title}
            title={card.title}
            value={loading ? '...' : card.value}
            hint={card.hint}
            icon={card.icon}
            onClick={() => openDetail(card)}
          />
        ))}
      </div>

      <KpiDetailPanel
        detail={detail}
        onClose={() => setDetail(null)}
        onOpenModule={() => {
          if (detail?.moduleRoute) {
            navigate(detail.moduleRoute);
          }
        }}
        className="border-primary/20 bg-primary/5"
      />

      {detail && (
        <Card>
          <CardHeader>
            <CardTitle>{drilldownMetric} - Detailed Page</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-hr-sm">
                <thead>
                  <tr>
                    {Object.keys(drilldownRows[0] || { info: 1 }).map((column) => (
                      <th key={column} className="p-2 text-left capitalize">{column.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drilldownTable.data.map((row, idx) => (
                    <tr key={idx} className="border-t">
                      {Object.keys(drilldownRows[0] || { info: 1 }).map((column) => (
                        <td key={`${idx}-${column}`} className="p-2">{String(row[column] ?? '-')}</td>
                      ))}
                    </tr>
                  ))}
                  {drilldownTable.data.length === 0 && (
                    <tr>
                      <td className="p-3 text-center text-muted-foreground">No drill-down data found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="pt-3">
              <TableControls
                totalRows={drilldownTable.totalRows}
                page={drilldownTable.page}
                totalPages={drilldownTable.totalPages}
                pageSize={drilldownTable.pageSize}
                pageSizes={drilldownTable.pageSizes}
                onPageSizeChange={drilldownTable.onPageSizeChange}
                onPrevious={drilldownTable.goToPreviousPage}
                onNext={drilldownTable.goToNextPage}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Trend Periods</CardTitle>
        </CardHeader>
        <CardContent className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="date" hide={trend.length > 18} {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip contentStyle={tooltipContentStyle} />
              <Legend />
              <Line type="monotone" dataKey="attendance_marks" stroke={CHART_COLORS[0]} name="Attendance" />
              <Line type="monotone" dataKey="helpdesk_tickets" stroke={CHART_COLORS[1]} name="Helpdesk" />
              <Line type="monotone" dataKey="survey_responses" stroke={CHART_COLORS[3]} name="Survey Responses" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Export Packs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {exportPacks.map((pack) => (
            <div key={pack.id} className="border rounded-md p-3 flex items-center justify-between">
              <div>
                <p className="font-medium">{pack.file_name || `Pack #${pack.id}`}</p>
                <p className="text-hr-xs text-muted-foreground">{pack.period_key} • {pack.status}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleDownloadPack(pack.id)}>
                Download
              </Button>
            </div>
          ))}
          {exportPacks.length === 0 && (
            <EmptyState compact title="No export packs yet" description="Generate an export pack to download consolidated analytics." icon={BarChart3} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExecutiveAnalyticsDashboard;
